<template>
  <div>
    <b-sidebar
      id="sidebar-extraordinary-change"
      sidebar-class="sidebar-lg"
      :visible="extraordinaryChangeSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="val => mutateExtraordinaryChangeSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Alteração extraordinária do contrato {{ extraordinaryChangeSidebar.id }}</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
        >
          <!-- Primeira camada do aviso de impacto: permanente, sempre visível -->
          <b-alert
            variant="warning"
            show
            class="mb-2"
          >
            <div class="alert-body">
              <feather-icon
                icon="AlertTriangleIcon"
                size="16"
                class="mr-50"
              />
              <span>{{ impactWarning }}</span>
            </div>
          </b-alert>
          <b-row>
            <b-col>
              <b-form-group
                label="Competência afetada"
                label-for="extraordinary-change-competence"
              >
                <b-form-input
                  id="extraordinary-change-competence"
                  :value="competenceText"
                  type="text"
                  readonly
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Categoria"
                label-for="extraordinary-change-category"
              >
                <v-select
                  id="extraordinary-change-category"
                  v-model="category"
                  :reduce="option => option.value"
                  :options="categories"
                  :loading="loadingCategories"
                  label="label"
                  :class="getSelectErrorClass(v$.category.$error)"
                >
                  <template #no-options>
                    Sem resultados
                  </template>
                </v-select>
                <div class="invalid-feedback">
                  <span v-if="v$.category.$error">
                    Você deve informar a categoria da alteração
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Número do chamado"
                label-for="extraordinary-change-ticket"
              >
                <b-form-input
                  id="extraordinary-change-ticket"
                  v-model="ticket_number"
                  type="text"
                  :class="{ 'is-invalid': v$.ticket_number.$error }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.ticket_number.$error">
                    Você deve informar o número do chamado (mínimo de 3 caracteres)
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Motivo da alteração"
                label-for="extraordinary-change-reason"
              >
                <b-form-textarea
                  id="extraordinary-change-reason"
                  v-model="reason"
                  rows="3"
                  placeholder="Descreva o motivo da alteração em uma competência já encerrada"
                  :class="{ 'is-invalid': v$.reason.$error }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.reason.$error">
                    Você deve informar o motivo com no mínimo 20 caracteres
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Impacto esperado"
                label-for="extraordinary-change-impact"
              >
                <b-form-checkbox-group
                  id="extraordinary-change-impact"
                  v-model="expected_impact"
                  :options="impactOptions"
                  stacked
                />
                <div
                  v-if="v$.expected_impact.$error"
                  class="text-danger small mt-50"
                >
                  Você deve informar ao menos um impacto esperado
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Descrição da correção"
                label-for="extraordinary-change-correction"
              >
                <b-form-textarea
                  id="extraordinary-change-correction"
                  v-model="correction_description"
                  rows="3"
                  placeholder="Descreva o que será corrigido no contrato"
                  :class="{ 'is-invalid': v$.correction_description.$error }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.correction_description.$error">
                    Você deve descrever a correção com no mínimo 20 caracteres
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <!-- Form Actions -->
              <div class="d-flex">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  :disabled="saving"
                  variant="primary"
                  class="mr-2"
                  type="submit"
                >
                  {{ saving ? "Registrando..." : "Registrar e continuar" }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckboxGroup,
  BButton,
  BAlert,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import useVuelidate from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'
import { useToast } from 'vue-toastification/composition'
import useAppConfig from '@core/app-config/useAppConfig'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getVueSelectErrorClass } from '@/helpers/validators'
import { apiErrorToast } from '@/helpers/error_messages'
import { EXPECTED_IMPACT_OPTIONS, IMPACT_NONE } from '@/constants/contract_closing'
import * as closingTypes from '@/modules/contract_closing/store/types'
import { competenceLabel } from '../utils/contractLock'
import * as types from '../store/types'

// Aviso obrigatório do card: aparece em duas camadas (alerta fixo + confirmação
// bloqueante no envio).
const IMPACT_WARNING = 'Atenção: esta competência já está encerrada. A alteração poderá '
  + 'impactar Faturômetro, rankings, comissões e relatórios financeiros. Confirme que as '
  + 'validações necessárias foram realizadas antes de continuar.'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckboxGroup,
    BButton,
    BAlert,
    BRow,
    BCol,
    vSelect,
  },
  directives: {
    Ripple,
  },
  setup() {
    const { skin } = useAppConfig()

    return { toast: useToast(), skin, v$: useVuelidate() }
  },
  data() {
    return {
      saving: false,
      loadingCategories: false,
      impactWarning: IMPACT_WARNING,
      impactOptions: EXPECTED_IMPACT_OPTIONS,
      category: undefined,
      ticket_number: undefined,
      reason: undefined,
      correction_description: undefined,
      expected_impact: [],
    }
  },
  validations() {
    return {
      category: { required },
      ticket_number: { required, minLength: minLength(3) },
      reason: { required, minLength: minLength(20) },
      correction_description: { required, minLength: minLength(20) },
      expected_impact: { required, minLength: minLength(1) },
    }
  },
  computed: {
    ...mapGetters({
      extraordinaryChangeSidebar: types.EXTRAORDINARY_CHANGE_SIDEBAR,
      categories: closingTypes.CLOSING_EXCEPTION_CATEGORIES,
    }),
    competenceText() {
      const label = competenceLabel(this.extraordinaryChangeSidebar.competence)

      return label || 'Competência não identificada'
    },
  },
  watch: {
    // As categorias só são buscadas quando a sidebar abre de fato — o componente fica
    // montado em toda a listagem de contratos.
    'extraordinaryChangeSidebar.visible': function onVisibleChange(visible) {
      if (visible) this.loadCategories()
    },
    // "Nenhum" é exclusivo: não faz sentido junto com outro impacto.
    expected_impact(current, previous) {
      const added = (current || []).filter(item => !(previous || []).includes(item))
      if (added.includes(IMPACT_NONE)) {
        this.expected_impact = [IMPACT_NONE]
      } else if (added.length && current.includes(IMPACT_NONE)) {
        this.expected_impact = current.filter(item => item !== IMPACT_NONE)
      }
    },
  },
  methods: {
    ...mapMutations({
      mutateExtraordinaryChangeSidebar: types.MUTATE_EXTRAORDINARY_CHANGE_SIDEBAR,
    }),
    ...mapActions({
      getCategories: closingTypes.GET_CLOSING_EXCEPTION_CATEGORIES,
      saveClosingException: closingTypes.SAVE_CLOSING_EXCEPTION,
    }),
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError)
    },
    loadCategories() {
      if (this.categories && this.categories.length) return
      this.loadingCategories = true
      this.getCategories()
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              'Ocorreu um erro ao carregar as categorias para seleção. Entre em contato com o setor de TI.',
            ),
          })
        })
        .finally(() => {
          this.loadingCategories = false
        })
    },
    clear() {
      this.category = undefined
      this.ticket_number = undefined
      this.reason = undefined
      this.correction_description = undefined
      this.expected_impact = []
      this.v$.$reset()
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate()
      if (!isFormCorrect) return
      const confirmation = await this.$swal({
        title: 'Alteração extraordinária',
        text: IMPACT_WARNING,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar alteração',
        cancelButtonText: 'Cancelar',
        background: `${this.skin === 'dark' ? '#283046' : ''}`,
        customClass: {
          // Classe própria (e estável) para este diálogo: rotinas que dispensam avisos
          // clicando em todo `.swal2-confirm` visível não podem confirmá-lo sem querer.
          popup: 'swal-extraordinary-change',
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary ml-1',
          htmlContainer: `${this.skin === 'dark' ? 'text-white' : ''}`,
        },
      })
      if (!confirmation.value) return
      this.saving = true
      const {
        id,
        competence,
        changes,
      } = this.extraordinaryChangeSidebar
      this.saveClosingException({
        contract_id: id,
        competence,
        category: this.category,
        ticket_number: this.ticket_number,
        reason: this.reason,
        // O backend valida este campo como string; o relatório o lê de volta com
        // `split(',')` sobre os valores crus.
        expected_impact: this.expected_impact.join(', '),
        correction_description: this.correction_description,
        // Omite `changes` se vazio para manter a autorização pendente. 
        // Enviar `[]` fazia o backend consumi-la na hora, quebrando o PUT.
        ...(changes && Object.keys(changes).length ? { changes } : {}),
      })
        .then(response => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso',
              text: 'A justificativa foi registrada. Prossiga com a alteração do contrato.',
              icon: 'CoffeeIcon',
              variant: 'success',
            },
          })
          const { proceed } = this.extraordinaryChangeSidebar
          this.mutateExtraordinaryChangeSidebar({ visible: false })
          // Passo 2: quem abriu a sidebar executa a mutação real carregando o
          // `exception_id` recém-criado.
          if (proceed) {
            proceed({
              exception_id: response.data?.id,
              competence: response.data?.competence || competence,
            })
          }
        })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              'Ocorreu um erro ao registrar a alteração extraordinária. Entre em contato com o setor de TI.',
            ),
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
